// Prédicats purs sur l'état, réutilisés par le coureur ET les moines.
// Aucun import externe, aucun RNG, aucune horloge : déterminisme total.
import { crateAt, GameState, Monk } from './state'
import { SUB, T, cellIndex } from './types'

// Tuile à (col, row). Hors grille : Concrete (murs virtuels, on ne sort jamais).
export function tileAt(s: GameState, col: number, row: number): T {
  if (col < 0 || col >= s.cols || row < 0 || row >= s.rows) return T.Concrete
  return s.terrain[cellIndex(col, row, s.cols)] as T
}

// Bloque le passage : Brick, Concrete, Ice (pleine côté passage, cf. Task 10) et Crumble
// (le sol friable porte comme une brique ; son effritement viendra en Phase 1 Task 3).
// ConveyorL/ConveyorR/Trampoline sont SOLIDES : on se tient dessus (leurs effets, dérive
// et rebond, arrivent en Phase 2 Tasks 2/3). Lava n'est PAS solide : on tombe dedans.
// DoorClosed est SOLIDE : la porte fermée bloque le passage (son ouverture à la clé
// arrive en Phase 3 Tasks 2/3 ; ici elle se comporte comme un mur).
export const isSolid = (t: T) =>
  t === T.Brick || t === T.Concrete || t === T.Ice || t === T.Crumble ||
  t === T.ConveyorL || t === T.ConveyorR || t === T.Trampoline ||
  t === T.DoorClosed ||
  t === T.TrampolineL || t === T.TrampolineR || // Lot C : trampolines directionnels (solides, on se tient dessus)
  t === T.RampUpRight || t === T.RampUpLeft || // RAMPES (16/07) : solides (on se tient dessus ; la montee/descente = hop diagonal de moveRunner)
  t === T.TrampolineStrong || // trampoline puissant (16/07) : solide comme les autres trampolines
  t === T.TrampolineStrongL || t === T.TrampolineStrongR || // directionnels PUISSANTS (16/07 soir, C1)
  t === T.Barrier // BARRIÈRE FERMÉE (17/07) : un mur tant que le circuit n'est pas pressé (BarrierOpen, elle, est traversable)
  // NB : ConveyorUp/Down et BombRefill sont NON solides (on entre dedans / on se tient dessus via le sol).

// SENS UNIQUE (17/07) : '»'/'«' sont non solides mais leur ENTRÉE à contresens est un mur —
// pour tout mouvement HORIZONTAL (marche, nage, dash, dérive, rebond, BFS moine). dir = le sens
// du déplacement de l'entité (+1 vers la droite). La chute verticale, elle, les traverse.
export const blocksFrom = (t: T, dir: -1 | 1): boolean =>
  isSolid(t) || (t === T.OneWayR && dir === -1) || (t === T.OneWayL && dir === 1)

// Échelle « climbable » : l'échelle normale ET l'échelle de sortie révélée (Task 7).
// Toute logique support/montée/descente passe par ici pour que l'ExitLadder se
// comporte exactement comme une Ladder. isSolid n'inclut PAS ExitLadder.
export const isLadder = (t: T) => t === T.Ladder || t === T.ExitLadder

// Une entité peut occuper la case si elle n'est pas solide
// (Empty, Ladder, Rope, FalseBrick, Sand, Teleporter...).
export function isEnterable(s: GameState, col: number, row: number): boolean {
  return !isSolid(tileAt(s, col, row))
}

// Moine vivant piégé dans une case-trou donnée (sinon null).
export function trappedMonkAt(s: GameState, cell: number): Monk | null {
  return s.monks.find((m) => m.respawnAt === 0 && m.trappedCell === cell) ?? null
}

// Porte-t-elle l'entité ? Support si :
// sa case est une échelle, OU une corde alignée, OU la case dessous est solide,
// échelle, ou contient un moine piégé. Le SABLE ne porte JAMAIS (Règle FINALE T10) :
// toute entité dont la case est du sable s'y enfonce (cf. sim.ts / monks.ts).
// La case `c` (à la rangée `row`) a-t-elle du sol dessous ? (solide, échelle, moine piégé ou caisse.)
function footAt(s: GameState, c: number, row: number): boolean {
  const below = tileAt(s, c, row + 1)
  return isSolid(below) || isLadder(below) ||
    trappedMonkAt(s, cellIndex(c, row + 1, s.cols)) !== null || !!crateAt(s, c, row + 1) // une caisse (Phase 4) porte comme un sol
}
// Support « DEUX BORDS » (21/07) : l'entité fait SUB de large ; elle est portée tant qu'une case
// solide/échelle/caisse/moine-piégé est sous l'UNE OU L'AUTRE de ses colonnes-bords (x>>3 et
// (x+SUB-1)>>3, exactement comme la collision de marche). C'est la règle des MOINES, du creusage et
// de la pose de bombe : le coureur, lui, tombe au CENTRE (hasSupportCenter). On garde « deux bords »
// ici car la chute des moines (fallMonk) recale au PLANCHER sans glisser vers le centre du trou :
// avec la règle centre ils décrocheraient à cheval côté solide et ne tomberaient JAMAIS dedans (fin
// du piégeage). Aligné (x % SUB === 0) : colR === col -> comportement STRICTEMENT inchangé.
export function hasSupport(s: GameState, e: { x: number; y: number }): boolean {
  const col = e.x >> 3
  const row = e.y >> 3
  const here = tileAt(s, col, row)
  if (isLadder(here)) return true
  if (here === T.Rope && e.y % SUB === 0) return true
  return footAt(s, col, row) || footAt(s, (e.x + SUB - 1) >> 3, row)
}

// Support « CENTRE » — règle de CHUTE du COUREUR uniquement (ressenti « Centre » validé par
// Claude-Alain sur la maquette public/pages/perso-chute-test.html). Le coureur est porté tant qu'une
// case le porte sous la colonne de son CENTRE ((x + SUB/2) >> 3) : il décroche dès que son centre
// franchit le bord du trou (à cheval, la moitié du corps au-dessus du vide), au lieu de flotter tant
// qu'un bord tient. Symétrique gauche/droite (le centre est le point médian). Sa chute glisse ensuite
// vers le centre du trou (dérive sens `face` / recalage plancher, cf. sim.ts) et l'atterrissage reste
// aligné dans la colonne du trou. Aligné (x % SUB === 0) : colC === col -> support inchangé (le
// coureur au repos, le creusage et la chute droite ne changent pas). Séparé de hasSupport car les
// MOINES doivent garder « deux bords » (leur chute n'a pas le glissé-vers-centre -> voir hasSupport).
export function hasSupportCenter(s: GameState, e: { x: number; y: number }): boolean {
  const col = e.x >> 3
  const row = e.y >> 3
  const here = tileAt(s, col, row)
  if (isLadder(here)) return true
  if (here === T.Rope && e.y % SUB === 0) return true
  return footAt(s, (e.x + SUB / 2) >> 3, row)
}

// L'entité repose sur de la glace (case dessous = Ice) et est alignée verticalement.
// Le « glissant » lui-même est géré en Task 10.
export function standsOnIce(s: GameState, e: { x: number; y: number }): boolean {
  return tileAt(s, e.x >> 3, (e.y >> 3) + 1) === T.Ice && e.y % SUB === 0
}
